import filterAssembler from './map/filter_assembler'
import mapConverters from './map/map_converters'
import GlobalOverlayManager from './global_overlay_manager'

function Filter(parsedFilter, locationFields) {

  this.modified = false;
  // The list of expressions that make up the filter
  this._data = parsedFilter;
  // 'City', 'CountyOrParish', 'PostalCode' etc.
  this._locationFields = locationFields;

}

// returns an array containing the expressions from exA that are not present in exB
Filter.expressionsRemoved = function(exA, exB) {
  return this.expressionsAdded(exB, exA);
};

// returns an array containing the expressions from exB that are not present in exA
Filter.expressionsAdded = function(exA, exB) {
  var exC = [];

  // Use some instead of forEach so we can break out of the inner loop when we
  // find a match.  That way we go through fewer inner loops.
  exB.some(expressionB => {
    var match = false;
    exA.some(expressionA => {
      try {
        if(expressionB.field === expressionA.field){
          if (expressionB.type === expressionA.type) {
            if (expressionB.type === 'shape') {
              if (expressionB.function_name === expressionA.function_name) {
                if (expressionB.function_parameters.toString() === expressionA.function_parameters.toString()) {
                  // For shape expressions, also check if operators and conjunctions match
                  // Otherwise the expression has changed.  Remove the old, add the new.
                  if (expressionB.operator === expressionA.operator && 
                      expressionB.conjunction === expressionA.conjunction) {
                    match = true;
                    return true; // Break out of the inner loop
                  }
                }
              }
            } else if (expressionB.value.sort().toString().replace(/'/g, '') === expressionA.value.sort().toString().replace(/'/g, '')) {
              // For MapOverlayShape expressions, also check if operators and conjunctions match
              if (expressionB.field === 'MapOverlayShape') {
                  // For overlayshape expressions, also check if operators and conjunctions match
                  // Otherwise the expression has changed.  Remove the old, add the new.
                  if (expressionB.operator === expressionA.operator &&
                    expressionB.conjunction === expressionA.conjunction) {
                  match = true;
                  return true; // Break out of inner loop
                }
              } else {
                // Value matches for non-MapOverlayShape expressions
                match = true;
                return true; // Break out of inner loop
              }
            }
          }
        }
      } catch(e) {
        match = false;
      }
    }); // end of inner loop
    if(!match){
      exC.push(expressionB);
    }
  }) // end of outer loop

  return exC;
};

Filter.prototype = {
  constructor: Filter,

  setData: function(newData) {
    if(newData !== this._data){
      this._data = newData;
      this.modified = true;
    }
  },

  expressions: function() {
    return this._data;
  },

  shapes: function() {
    return this._data.filter(function(element){
      return ( element.type === "shape" && element.function_name !== null );
    });
  },

  overlays: function() {
    return this._data.filter(function(element){
      return element.field === "MapOverlay";
    });
  },

  overlayShapes: function() {
    return this._data.filter(function(element) {
      return element.field === "MapOverlayShape";
    })
  },

  locations: function() {
    var self = this;
    var locationFields = this._data.filter(function(element){
      return self._locationFields.indexOf(element.field) > -1;
    });
    // Overlays and overlay shapes are location fields.
    // Include them with the return values.
    var overlays = this.overlays();
    var overlayShapes = this.overlayShapes();
    return locationFields.concat(overlays).concat(overlayShapes);
  },

  addDrawnShapeToFilter: function(expression) {
    // Add a drawn shape to the filter when the filter panel is not loaded.
    // This function determines where a new drawn shape should be added based on:
    // 1. Whether an intersection block group exists
    // 2. Whether a block group exists with other drawn shapes
    // 3. Whether a within block group exists
    // 4. Otherwise creates a new block group
    this._addShapeToFilterWithoutPanel(expression, 'drawn', null, null);
  },

  addOverlayShapeToFilter: function(expression, overlayId) {
    // Add an overlay shape to the filter when the filter panel is not loaded.
    // This function determines where a new overlay shape should be added based on:
    // 1. Whether an intersection block group exists
    // 2. Whether a block group exists with other overlay shapes from the same overlay parent
    // 3. Whether a within block group exists
    // 4. Otherwise creates a new block group

    // Build a map of overlay shapes from existing expressions for Shape Group matching
    // This allows overlay shapes from the same overlay parent to be grouped together
    let overlayShapeMap = {};
    if (overlayId) {
      this._data
        .filter(expr => expr.field === 'MapOverlayShape')
        .forEach(expr => {
          const shapeId = expr.value?.[0] || expr.condition?.replace(/'/g, '');
          if (shapeId) {
            const existingShape = GlobalOverlayManager.getLoadedMyMapOverlayShape(shapeId);
            if (existingShape && existingShape.OverlayId) {
              overlayShapeMap[shapeId] = existingShape.OverlayId;
            }
          }
          // The overlay shape should always be available here.  It is the only way we can add
          // an overlay to the filter without the fitler panel.
        });
    }

    this._addShapeToFilterWithoutPanel(expression, 'overlay', overlayId, overlayShapeMap);
  },

  addPolygon: function(pointsArray, polygonName, polygonColor, desiredConjunction) {

    const pointsString = mapConverters.getStringFromPoints(pointsArray);

    let expression = {
      block_group: 0,
      level: 0,
      condition: "polygon('" + pointsString + "')",
      conjunction: "And",
      conjunction_level: 0,
      field: 'Location',
      function_name: 'polygon',
      function_parameters: [ pointsString ],
      operator: 'Eq',
      type: 'shape',
      unary_operator: null,
      value: [{
       type: 'LineString',
       coordinates: []
      }]
    }

    let annotation = {
      Type: 'Shape',
      Match: {
        Substring: expression.condition
      },
      Attributes: {
        Label: polygonName,
        Color: polygonColor.replace('#','')
      }
    }

    pointsArray.forEach((latLng) => {
      expression.value[0].coordinates.push([latLng.lat(), latLng.lng()]);
    });

    this.addDrawnShapeToFilter(expression);

    this.modified = true;

    if( window.nativeAppInterface && typeof window.nativeAppInterface.addShape === 'function' && window.nativeAppInterface.isNative()) {
      const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};

      window.nativeAppInterface.addShape({
        quickSearchId,
        savedSearchId,
        comparableSearchId,
        shape: expression,
        annotation
      });
    }

    return { expression: expression, annotation: annotation};
  },

  addRectangle: function(bounds, rectangleName, rectangleColor, desiredConjunction) {

    const pointsString = mapConverters.getStringFromPoints([bounds.getNorthEast(), bounds.getSouthWest()]);

    let expression = {
      block_group: 0,
      level: 0,
      condition: "rectangle('" + parseFloat(bounds.getNorthEast().lat().toFixed(6)) + " " + parseFloat(bounds.getNorthEast().lng().toFixed(6)) + "," + parseFloat(bounds.getSouthWest().lat().toFixed(6)) + " " + parseFloat(bounds.getSouthWest().lng().toFixed(6)) +  "')",
      conjunction: "And",
      conjunction_level: 0,
      field: 'Location',
      function_name: 'rectangle',
      function_parameters: [ pointsString ],
      operator: 'Eq',
      type: 'shape',
      unary_operator: null
    }
    
    this.addDrawnShapeToFilter(expression);

    this.modified = true;

    let annotation = {
      Type: 'Shape',
      Match: {
        Substring: expression.condition
      },
      Attributes: {
        Label: rectangleName,
        Color: rectangleColor.replace('#','')
      }
    }

    if(window.nativeAppInterface && typeof window.nativeAppInterface.addShape === 'function' && window.nativeAppInterface.isNative()) {
      const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};

      window.nativeAppInterface.addShape({
        quickSearchId,
        savedSearchId,
        comparableSearchId,
        shape: expression,
        annotation
      });
    }

    return {expression: expression, annotation: annotation};
  },

  addRadius: function(lat, lon, radius, radiusName, radiusColor) {

    let expression = {
      block_group: 0,
      level: 0,
      condition: "radius('" + parseFloat(lat.toFixed(6)) + " " + parseFloat(lon.toFixed(6)) + "'," + parseFloat(radius.toFixed(2)) + ")",
      function_parameters: [ parseFloat(lat.toFixed(6)) + " " + parseFloat(lon.toFixed(6)), parseFloat(radius.toFixed(2)) ],
      conjunction: "And",
      conjunction_level: 0,
      field: 'Location',
      function_name: 'radius',
      operator: 'Eq',
      type: 'shape',
      unary_operator: null
    }
    this.addDrawnShapeToFilter(expression);

    this.modified = true;

    let annotation = {
      Type: 'Shape',
      Match: {
        Substring: expression.condition
      },
      Attributes: {
        Label: radiusName,
        Color: radiusColor.replace('#','')
      }
    }

    if(window.nativeAppInterface && typeof window.nativeAppInterface.addShape === 'function' && window.nativeAppInterface.isNative()) {
      const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};

      window.nativeAppInterface.addShape({
        quickSearchId,
        savedSearchId,
        comparableSearchId,
        shape: expression,
        annotation
      });
    }
    return {expression: expression, annotation: annotation};
  },

  toString: function(){
    return filterAssembler(this._data);
  },

  removeShapeByCondition: function(condition) {
    for (var x = 0; x < this._data.length; x++) {
      if (this._data[x].condition == condition) {
        // Check if this is a MapOverlayShape and if there are other MapOverlayShapes
        if (this._data[x].field === 'MapOverlayShape') {
          // Count remaining MapOverlayShapes after removal
          let remainingMapOverlayShapes = this._data.filter((item, index) =>
            index !== x && item.field === 'MapOverlayShape'
          );

          // If there are remaining MapOverlayShapes, we need to adjust conjunctions
          if (remainingMapOverlayShapes.length > 0) {
            // Find the first remaining MapOverlayShape and set its conjunction to 'And'
            // This ensures proper logical structure when removing from an 'Or' group
            for (let y = 0; y < this._data.length; y++) {
              if (y !== x && this._data[y].field === 'MapOverlayShape') {
                this._data[y].conjunction = 'And';
                break;
              }
            }
          }
        }

        this.removeShape(this._data[x]);
        return;
      }
    }
  },

  removeShape: function(element) {
    var index = this._data.indexOf(element);
    if (index >= 0) {
      this._data.splice(index, 1);
      this.modified = true;

      if(window.nativeAppInterface && typeof window.nativeAppInterface.removeShape === 'function' && window.nativeAppInterface.isNative()) {
        const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};
        window.nativeAppInterface.removeShape({
          quickSearchId,
          savedSearchId,
          comparableSearchId,
          shapeId: element.condition,
        });
      }
    }

    if (this.modified) {
      for (var x = 0; x < this._data.length; x++) {
        if (this._data[x].type == 'shape' || this._data[x].field === 'MapOverlayShape') {
          // The first drawn shape or MapOverlayShape should have a conjunction of And.
          // If the removed item was the first drawn shape or MapOverlayShape, we need to make sure
          // that the new first drawn shape or MapOverlayShape has the right conjunction.
          this._data[x].conjunction = 'And';
          // If this shape is in a grouped block (level 1), it should also have conjunction_level 0
          // to properly anchor the group to the rest of the filter
          if (this._data[x].level === 1) {
            this._data[x].conjunction_level = 0;
          }
          break;
        }
      }
    }
  },

  removeShapes: function() {
    var self = this;

    var shapeRemoved = false;

    this._data.forEach(function(element, index, dataArray){
      if(element.type === 'shape'){
        dataArray.splice(index, 1);
        self.modified = true;
        shapeRemoved = true;

         if(window.nativeAppInterface && typeof window.nativeAppInterface.removeShape === 'function' && window.nativeAppInterface.isNative()) {
          const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};
          window.nativeAppInterface.removeShape({
            quickSearchId,
            savedSearchId,
            comparableSearchId,
            shapeId: element.condition,
          });
        }
      }
    });

  },

  // Now that we can edit shapes directly, we need a way to replace
  // the values in the associated expression instead of deleting the
  // old and inserting a new one.
  replaceShape: function(oldCondition, newCondition, newFunctionParams) {
    for (var x = 0; x < this._data.length; x++) {
      if (this._data[x].condition == oldCondition) {
        this._data[x].condition = newCondition;
        this._data[x].function_parameters = newFunctionParams;
        this.modified = true;

        if(window.nativeAppInterface && typeof window.nativeAppInterface.replaceShape === 'function' && window.nativeAppInterface.isNative()) {
          const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};

          window.nativeAppInterface.replaceShape({
            quickSearchId,
            savedSearchId,
            comparableSearchId,
            shapeId: oldCondition,
            shape: this._data[x]
          });
        }
        return;
      }
    }
  },

  addMapOverlayShapeToFilter: function(overlayShape, desiredConjunction) {
    let expression = {
      type: 'character',
      block_group: 0,
      condition: "'" + overlayShape.Id + "'",
      conjunction: "And",
      conjunction_level: 0,
      custom_field: false,
      field: 'MapOverlayShape',
      level: 0,
      operator: 'Eq',
      value: [overlayShape.Id]
    }

    let annotation = {
      Type: 'Overlay',
      Match: {
        Substring: expression.condition
      },
      Attributes: {
        Shape: overlayShape.Id,
        Overlay: overlayShape.OverlayId,
        OverlayName: overlayShape.OverlayName,
        ShapeName: overlayShape.Name
      }
    }

    this.addOverlayShapeToFilter(expression, overlayShape.OverlayId)
    this.modified = true;
    return { expression: expression, annotation: annotation};
  },


  removeMapOverlayShape: function(id) {
    let modified = false;
    this._data.forEach(function(element, index, dataArray){
      if (element.condition.includes(id)) {
        dataArray.splice(index, 1)
        modified = true;
      }
    });

    if (modified) {
      this.modified = true;
      for (var x = 0; x < this._data.length; x++) {
        if (this._data[x].field == 'MapOverlayShape') {
          // The first shape should have a conjunction of And.
          // If the first shape was removed, we need to make sure
          // that the new first shape has the right conjunction.
          this._data[x].conjunction = 'And';
          break;
        }
      }
    }

  },

  propertyType: function() {
    var propertyTypes = [];

    this._data.forEach(function(element, index, dataArray){
      if(element.field === 'PropertyType'){
        element.value.forEach((value) => {
          propertyTypes.push(value.replace(/'/g, ''));
        })
      }
    });

    return propertyTypes;
  },

  propertyClass: function() {
    var propertyClasses = [];

    this._data.forEach(function(element, index, dataArray){
      if(element.field === 'PropertyClass'){
        element.value.forEach((value) => {
          propertyClasses.push(value.replace(/'/g, ''));
        })
      }
    });

    return propertyClasses;
  },


  equalToOriginal: function(ignoreRemovedDataSource = false) {
    let removedExpressions = Filter.expressionsRemoved(mapSupport.parsedFilter, this._data)
    let addedExpressions = Filter.expressionsAdded(mapSupport.parsedFilter, this._data)
    let equal = true
    if (removedExpressions.length > 0) {
      if (removedExpressions.length > 1 || !ignoreRemovedDataSource || removedExpressions[0].field !== 'MlsId') {
        equal = false;
      }
    }
    if (addedExpressions.length > 0) {
      equal = false;
    }
    return equal;
  },

  // Unified method to analyze shapes based on different criteria
  _analyzeShapes: function(options) {
    options = options || {};
    var includeDrawnShapes = options.includeDrawnShapes !== false; // default true
    var includeOverlayShapes = options.includeOverlayShapes !== false; // default true
    var includeOverlayCompatible = options.includeOverlayCompatible || false;

    var result = {
      hasDrawnShapes: false,
      hasOverlayShapes: false,
      hasCompatibleShapes: false,
      lastDrawnShapeIndex: -1,
      lastOverlayShapeIndex: -1,
      firstCompatibleShapeIndex: -1,
      lastCompatibleShapeIndex: -1,
      lastCompatibleOverlayIndex: -1,
      maxBlockGroup: 0,
      lastBlockGroup: 0,
      lastBlockGroupLevel: 0,
      hasIntersectionGroup: false,
      intersectionGroupId: -1,
      intersectionGroupItems: []
    };

    var data = this._data;
    var length = data.length;

    if (length === 0) {
      return result;
    }

    // Single pass: Group items by block_group while tracking shapes and maxBlockGroup
    const blockGroups = {};
    var lastExpression = data[length - 1];
    result.lastBlockGroup = lastExpression.block_group || 0;
    result.lastBlockGroupLevel = lastExpression.level || 0;

    for (var i = 0; i < length; i++) {
      var item = data[i];
      const blockGroup = item.block_group || 0;

      // Group by block_group
      if (!blockGroups[blockGroup]) {
        blockGroups[blockGroup] = [];
      }
      // Copy item into a new object and push it onto the blockGroups array
      blockGroups[blockGroup].push({ ...item, index: i });

      // Track maxBlockGroup
      if (blockGroup > result.maxBlockGroup) {
        result.maxBlockGroup = blockGroup;
      }

      // Track drawn shapes
      if (includeDrawnShapes && item.type === 'shape') {
        result.hasDrawnShapes = true;
        result.lastDrawnShapeIndex = i;
      }

      // Track overlay shapes
      if (includeOverlayShapes && item.field === 'MapOverlayShape') {
        result.hasOverlayShapes = true;
        result.lastOverlayShapeIndex = i;
      }
    }

    // Analyze block groups for compatibility (only if needed)
    if (includeOverlayCompatible) {
      for (const [blockGroupId, groupItems] of Object.entries(blockGroups)) {
        const isGroupCompatible = this._isBlockGroupCompatible(groupItems);

        if (isGroupCompatible) {
          result.hasCompatibleShapes = true;
          if (result.firstCompatibleShapeIndex === -1) {
            result.firstCompatibleShapeIndex = groupItems[0].index;
          }
          result.lastCompatibleShapeIndex = groupItems[groupItems.length - 1].index;

          // Track the last compatible overlay specifically
          // Iterate backwards for efficiency (find last overlay)
          for (var j = groupItems.length - 1; j >= 0; j--) {
            if (groupItems[j].field === 'MapOverlayShape') {
              result.lastCompatibleOverlayIndex = groupItems[j].index;
              break;
            }
          }

          // Check if this is an intersection group
          if (this._isIntersectionGroup(groupItems)) {
            result.hasIntersectionGroup = true;
            result.intersectionGroupId = parseInt(blockGroupId);
            result.intersectionGroupItems = groupItems;
          }
        }
      }
    }

    return result;
  },

  _isBlockGroupCompatible: function(groupItems) {
    // A block group is compatible if:
    // 1. All items are location-based (drawn shapes or MapOverlayShapes)
    // 2. All items have operator 'Eq'
    // This is only used for overlay shapes.  That is why the operator has to be 'Eq'.

    return groupItems.every(item => {
      const isLocationBased = (item.type === 'shape' && item.field === 'Location') ||
                             (item.field === 'MapOverlayShape');
      const hasEqOperator = item.operator === 'Eq';
      return isLocationBased && hasEqOperator;
    });
  },

  _isIntersectionGroup: function(groupItems) {
    // A group is an intersection group if:
    // 1. It has more than 1 shape (drawn or overlay)
    // 2. All shapes use 'And' conjunctions to connect them

    if (groupItems.length <= 1) {
      return false;
    }

    // Check if all shapes use 'And' conjunction
    return groupItems.every(item => item.conjunction === 'And');
  },

  // This method is used to determine the initial operator for
  // drawn shapes (locations). The operator is determined by analyzing
  // the filter expressions block groups and the conjunctions and operators
  // used by MapOverlayShapes and Locations. (We need to include MapOverlayShapes
  // because they can be in the same block group as Locations.)
  drawnShapesOperator: function() {
    // Find Location field expressions
    const locationExpressions = this._data.filter(expr => expr.field === 'Location');
    // Find MapOverlayShape field expressions
    const mapOverlayShapeExpressions = this._data.filter(expr => expr.field === 'MapOverlayShape');

    // Determine the proper operator based on expression analysis
    return this.determineDrawnShapesOperator(locationExpressions, mapOverlayShapeExpressions);
  },

  // Private helper method to determine the drawn shapes operator
  determineDrawnShapesOperator: function(locationExpressions, mapOverlayShapeExpressions) {
    // Collect all expressions from both fields
    const allExpressions = [...locationExpressions, ...mapOverlayShapeExpressions];

    if (allExpressions.length === 0) {
      return 'or';
    }

    // Group expressions by block_group
    const expressionsByBlock = this._groupByBlockGroup(allExpressions);

    // Analyze each block group
    for (const blockGroup in expressionsByBlock) {
      const groupItems = expressionsByBlock[blockGroup];
      // Extract just the expressions (without index) for compatibility with existing logic
      const expressions = groupItems.map(item => {
        const { index, ...expr } = item;
        return expr;
      });
      const blockGroupNum = parseInt(blockGroup);

      // For block_group > 0, skip the first conjunction as it's external
      if (blockGroupNum > 0 && expressions.length > 1) {
        // Get conjunctions starting from the second expression (internal conjunctions)
        const internalConjunctions = expressions.slice(1).map(expr => expr.conjunction);
        const uniqueInternalConjunctions = [...new Set(internalConjunctions)];
        const operators = expressions.map(expr => expr.operator);
        const uniqueOperators = [...new Set(operators)];

        // Rule 1: If individual comparisons use Eq and are joined by Or
        if (uniqueOperators.length === 1 && uniqueOperators[0] === 'Eq' &&
            uniqueInternalConjunctions.length === 1 && uniqueInternalConjunctions[0] === 'Or') {
          return 'or';
        }

        // Rule 2: If individual comparisons use Ne (should all be joined by And)
        if (uniqueOperators.length === 1 && uniqueOperators[0] === 'Ne' &&
            uniqueInternalConjunctions.length === 1 && uniqueInternalConjunctions[0] === 'And') {
          return 'not';
        }

        // Rule 3: If individual comparisons use Eq and are joined by And
        if (uniqueOperators.length === 1 && uniqueOperators[0] === 'Eq' &&
            uniqueInternalConjunctions.length === 1 && uniqueInternalConjunctions[0] === 'And') {
          return 'and';
        }
      } else {
        // For block_group 0 or single expressions - only concerned with shapes
        // In block group 0, shapes will always use And conjunction, so ignore conjunctions
        // Only examine Location field comparisons
        const locationExpressionsInBlock = expressions.filter(expr => expr.field === 'Location');

        // There should be 0 or 1 location expression in block 0
        if (locationExpressionsInBlock.length === 0) {
          return 'or';
        } else {
          // There should only be 1 location expression in this block
          // Only use the first one to determine the operator
          const operator = locationExpressionsInBlock[0].operator;

          // Simple rule: If comparison is Eq, operator is or. If comparison is Ne, operator is not.
          if (operator === 'Eq') {
            return 'or';
          } else if (operator === 'Ne') {
            return 'not';
          }
        }
        // If there are more than 1 location expressions, ignore them
      }
    }

    // Default fallback
    return 'or';
  },

  _addStandaloneDrawnShapeToFilter: function(expression) {
    expression.level = 0;
    expression.block_group = 0;
    expression.conjunction = 'And';
    this._data.push(expression);
  },

  _addDrawnShapeToOverlayGroup: function(expression, targetIndex, newBlockGroup) {
    // No drawn shapes.  But there are overlays.
    // OVERLAYS ARE CURRENTLY ALWAYS GROUPED TOGETHER WITH AN 'OR' CONJUNCTION.
    if (this._data[targetIndex].block_group == 0) {
      // Group the only overlay with the only (and new) drawn shape
      expression.level = 1;
      this._data[targetIndex].level = 1;
      expression.block_group = newBlockGroup;
      this._data[targetIndex].block_group = newBlockGroup;
      expression.conjunction = 'Or'; // No shapes.  The first one is an 'Or'
      expression.operator = 'Eq'
      this._data.splice(targetIndex + 1,0, expression);
    } else {
      // Add the only new drawn shape to the same group as the overlays (currently always in the same group and always 'Or)
      expression.level = this._data[targetIndex].level;
      expression.block_group = this._data[targetIndex].block_group;
      expression.conjunction = 'Or'; // No shapes.  The first one is an 'Or'
      expression.operator = 'Eq';
      this._data.splice(targetIndex + 1,0, expression);
    }
  },

  _addDrawnShapeToDrawnShapeGroup: function(expression, targetIndex, newBlockGroup) {
    let lastDrawnShapeExpression = this._data[targetIndex];
    // We have drawn shapes.  This should be in the same group as the other drawn shapes.
    if (lastDrawnShapeExpression.block_group == 0) {
      //  We have a single drawn shape at the top level of the filter.  We need to group it with the new shape
      lastDrawnShapeExpression.level = 1;
      expression.level = 1;

      lastDrawnShapeExpression.block_group = newBlockGroup;
      expression.block_group = newBlockGroup;
      
      lastDrawnShapeExpression.conjunction_level = 0;
      expression.conjunction_level = 1;

      lastDrawnShapeExpression.conjunction = 'And'; // TO connect all shapes to the rest of the filter
      expression.operator = lastDrawnShapeExpression.operator;
      if (lastDrawnShapeExpression.operator === 'Eq') {
        expression.conjunction = 'Or'; // No way to tell if it is Within or Intersection.  Default to Or.
      } else {
        expression.conjunction = 'And'; // If Ne, always join with And.
      }

      this._data.splice(targetIndex + 1,0, expression);
    } else {
      // Add this new shape to the group of existing shapes.  Use the desired conjunction if possible.
      expression.block_group = lastDrawnShapeExpression.block_group;

      expression.level = 1;
      expression.conjunction_level = 1;

      if (lastDrawnShapeExpression.conjunction_level === 0) {
        // Check if this is an intersection group (all shapes use 'And' conjunctions)
        var isIntersectionGroup = this._isIntersectionGroup(this._data.filter(item => item.block_group === lastDrawnShapeExpression.block_group));

        if (isIntersectionGroup) {
          expression.conjunction = 'And'; // Use 'And' for intersection groups
        } else if (lastDrawnShapeExpression.operator === 'Eq') {
          expression.conjunction = 'Or'; // Default to Or for union groups
        } else {
          expression.conjunction = 'And'; // If Ne, always join with And.
        }
      } else {
        expression.conjunction = lastDrawnShapeExpression.conjunction;
      }
      expression.operator = lastDrawnShapeExpression.operator;
      this._data.splice(targetIndex + 1,0, expression);
    }
  },

  // --------------------------------------------------------------------------
  // Internal function that handles the logic for adding shapes to filters when
  // the filter panel is not loaded.
  // --------------------------------------------------------------------------
  _addShapeToFilterWithoutPanel: function(shapeExpression, shapeType, overlayId, overlayShapeMap) {
    const expressions = this._data;

    // Determine the Shape Group identifier for the new shape
    const newShapeGroupId = shapeType === 'drawn' ? 'drawn' : overlayId;

    // Group expressions by block_group
    const blockGroups = this._groupByBlockGroup(expressions);

    // Find intersection block groups
    const intersectionBlockGroups = this._findIntersectionBlockGroups(blockGroups);

    // Find block groups that contain items from the same Shape Group
    const sameShapeGroupBlockGroups = this._findBlockGroupsWithSameShapeGroup(
      blockGroups,
      newShapeGroupId,
      shapeType,
      overlayShapeMap,
      expressions
    );

    // Find within block groups
    const withinBlockGroups = this._findWithinBlockGroups(blockGroups);

    // Determine where to add the shape
    let targetBlockGroup = null;
    let insertAfterIndex = -1;

    if (intersectionBlockGroups.length > 0) {
      // Rule 1: Add to intersection block group
      const intersectionGroup = intersectionBlockGroups[0];
      targetBlockGroup = intersectionGroup.blockGroupId;
      insertAfterIndex = intersectionGroup.items[intersectionGroup.items.length - 1].index;
    } else if (sameShapeGroupBlockGroups.length > 0) {
      // Rule 2: Add to block group with same Shape Group
      const sameGroup = sameShapeGroupBlockGroups[0];

      // Find the last item in this block group that has the same Shape Group ID
      // This ensures overlay shapes are grouped together, not mixed with Location shapes
      const matchingItems = sameGroup.items.filter(item => {
        const itemShapeGroupId = this._getShapeGroupIdForExpression(item, overlayShapeMap);
        return itemShapeGroupId === newShapeGroupId;
      });

      // Special case: If the same Shape Group is in block_group 0, we need to move it to a new block group
      if (sameGroup.blockGroupId === 0 && matchingItems.length > 0) {
        // Create a new block group
        const maxBlockGroup = Math.max(0, ...Object.keys(blockGroups).map(Number));
        targetBlockGroup = maxBlockGroup + 1;

        // Move all matching items from block_group 0 to the new block group
        const firstMatchingItem = matchingItems[0];
        const movedItems = [];
        matchingItems.forEach((item, idx) => {
          const expr = expressions[item.index];
          expr.block_group = targetBlockGroup;
          expr.level = 1;
          if (idx === 0) {
            expr.conjunction_level = 0;
            expr.conjunction = 'And'; // First item uses And to connect to rest of filter
          } else {
            expr.conjunction_level = 1;
            // Use the same conjunction logic as when adding to existing group
            if (firstMatchingItem.operator === 'Eq') {
              expr.conjunction = 'Or'; // Default to Or for union groups
            } else {
              expr.conjunction = 'And'; // If Ne, always join with And
            }
          }
          // Track moved items for _configureShapeExpression
          movedItems.push({ ...expr, index: item.index });
        });

        // Update blockGroups to include the new block group with moved items
        blockGroups[targetBlockGroup] = movedItems;

        insertAfterIndex = matchingItems[matchingItems.length - 1].index;
      } else {
        targetBlockGroup = sameGroup.blockGroupId;
        if (matchingItems.length > 0) {
          insertAfterIndex = matchingItems[matchingItems.length - 1].index;
        } else {
          // Fallback to last item in block group if no matching items found (shouldn't happen)
          insertAfterIndex = sameGroup.items[sameGroup.items.length - 1].index;
        }
      }
    } else if (withinBlockGroups.length > 0) {
      // Rule 3: Add to existing within block group
      const withinGroup = withinBlockGroups[0];

      // Special case: If the within group is in block_group 0, we need to move it to a new block group
      if (withinGroup.blockGroupId === 0) {
        // Create a new block group
        const maxBlockGroup = Math.max(0, ...Object.keys(blockGroups).map(Number));
        targetBlockGroup = maxBlockGroup + 1;

        // Move all shape items from block_group 0 to the new block group
        // Note: withinGroup.items includes ALL items in block_group 0 (MlsId, PropertyType, shapes)
        // We only want to move the shapes, not the non-shape expressions
        const shapeItems = withinGroup.items.filter(item =>
          (item.field === 'Location' && item.type === 'shape') ||
          item.field === 'MapOverlayShape'
        );

        if (shapeItems.length > 0) {
          const firstShapeItem = shapeItems[0];
          const movedItems = [];
          shapeItems.forEach((item, idx) => {
            const expr = expressions[item.index];
            expr.block_group = targetBlockGroup;
            expr.level = 1;
            if (idx === 0) {
              expr.conjunction_level = 0;
              expr.conjunction = 'And'; // First item uses And to connect to rest of filter
            } else {
              expr.conjunction_level = 1;
              // Use the same conjunction logic as when adding to existing group
              if (firstShapeItem.operator === 'Eq') {
                expr.conjunction = 'Or'; // Default to Or for union groups
              } else {
                expr.conjunction = 'And'; // If Ne, always join with And
              }
            }
            // Track moved items for _configureShapeExpression
            movedItems.push({ ...expr, index: item.index });
          });

          // Update blockGroups to include the new block group with moved items
          blockGroups[targetBlockGroup] = movedItems;

          insertAfterIndex = shapeItems[shapeItems.length - 1].index;
        } else {
          // No shapes to move (shouldn't happen if withinGroup was found)
          targetBlockGroup = maxBlockGroup + 1;
          insertAfterIndex = expressions.length > 0 ? expressions.length - 1 : -1;
        }
      } else {
        targetBlockGroup = withinGroup.blockGroupId;
        insertAfterIndex = withinGroup.items[withinGroup.items.length - 1].index;
      }
    } else {
      // Rule 4: Create new block group
      const maxBlockGroup = Math.max(0, ...Object.keys(blockGroups).map(Number));
      targetBlockGroup = maxBlockGroup + 1;
      insertAfterIndex = expressions.length > 0 ? expressions.length - 1 : -1; // Insert at end, or -1 if empty
    }

    // Configure the shape expression based on where it's being added
    this._configureShapeExpression(
      shapeExpression,
      targetBlockGroup,
      blockGroups[targetBlockGroup],
      expressions,
      insertAfterIndex
    );

    // Insert the expression into the filter
    const insertIndex = insertAfterIndex + 1;
    if (insertIndex < 0) {
      // Empty filter - just push
      expressions.push(shapeExpression);
    } else {
      expressions.splice(insertIndex, 0, shapeExpression);
    }

    this.modified = true;
  },

  // --------------------------------------------------------------------------
  // Group expressions by block_group
  // --------------------------------------------------------------------------
  _groupByBlockGroup: function(expressions) {
    const groups = {};
    expressions.forEach((expr, index) => {
      const blockGroup = expr.block_group || 0;
      if (!groups[blockGroup]) {
        groups[blockGroup] = [];
      }
      groups[blockGroup].push({ ...expr, index });
    });
    return groups;
  },

   // --------------------------------------------------------------------------
   // Find intersection block groups.
   // An intersection group has:
   // - More than 1 item
   // - ONLY shapes (Location shapes or MapOverlayShape)
   // - Operator: Eq
   // - 2nd item's conjunction: And
   // --------------------------------------------------------------------------
  _findIntersectionBlockGroups: function(blockGroups) {
    const intersectionGroups = [];

    for (const [blockGroupId, items] of Object.entries(blockGroups)) {
      if (items.length <= 1) continue;

      // Only consider groups that contain ONLY shapes (Location shapes or MapOverlayShape)
      const allAreShapes = items.every(item =>
        (item.field === 'Location' && item.type === 'shape') ||
        item.field === 'MapOverlayShape'
      );

      if (!allAreShapes) continue;

      const firstItem = items[0];
      const secondItem = items[1];

      // Check if this is an intersection group: Eq operator + And conjunction on 2nd item
      if (firstItem.operator === 'Eq' && secondItem.conjunction === 'And') {
        // Verify all items use And conjunction (as per _isIntersectionGroup logic)
        const allUseAnd = items.every(item => item.conjunction === 'And');
        if (allUseAnd) {
          intersectionGroups.push({
            blockGroupId: parseInt(blockGroupId),
            items: items
          });
        }
      }
    }

    return intersectionGroups;
  },

  // --------------------------------------------------------------------------
  // Find block groups that contain items from the same Shape Group as
  // the new shape
  // --------------------------------------------------------------------------
  _findBlockGroupsWithSameShapeGroup: function(blockGroups, newShapeGroupId, shapeType, overlayShapeMap, allExpressions) {
    const matchingGroups = [];

    // For overlay shapes, we need the overlayShapeMap to determine overlay groups
    // Check if overlayShapeMap is missing or empty (has no keys)
    if (shapeType === 'overlay' && (!overlayShapeMap || Object.keys(overlayShapeMap).length === 0)) {
      // Can't match by overlay group without overlayShapeMap or when it's empty
      return matchingGroups;
    }

    for (const [blockGroupId, items] of Object.entries(blockGroups)) {
      const hasMatchingShapeGroup = items.some(item => {
        const itemShapeGroupId = this._getShapeGroupIdForExpression(item, overlayShapeMap);
        return itemShapeGroupId === newShapeGroupId;
      });

      if (hasMatchingShapeGroup) {
        matchingGroups.push({
          blockGroupId: parseInt(blockGroupId),
          items: items
        });
      }
    }

    return matchingGroups;
  },

  // --------------------------------------------------------------------------
  // Get the Shape Group ID for an existing expression
  // --------------------------------------------------------------------------
  _getShapeGroupIdForExpression: function(expression, overlayShapeMap) {
    // For drawn shapes: field === 'Location' and type === 'shape'
    if (expression.field === 'Location' && expression.type === 'shape') {
      return 'drawn';
    }

    // For overlay shapes: field === 'MapOverlayShape'
    // We need to look up the OverlayId from the shape ID
    if (expression.field === 'MapOverlayShape') {
      // The expression has value: [shapeId] or condition: "'shapeId'"
      const shapeId = expression.value?.[0] || expression.condition?.replace(/'/g, '');
      if (shapeId && overlayShapeMap && overlayShapeMap[shapeId]) {
        return overlayShapeMap[shapeId];
      }
      // If we can't determine the overlay ID, we can't match by Shape Group
      return null;
    }
    return null;
  },

  // --------------------------------------------------------------------------
  // Find WITHIN block groups.
  // A WITHIN group has:
  // - Single item: operator === 'Eq'
  // - Multiple items: operator === 'Eq' AND 2nd item's conjunction === 'Or'
  // --------------------------------------------------------------------------
  _findWithinBlockGroups: function(blockGroups) {
    const withinGroups = [];

    for (const [blockGroupId, items] of Object.entries(blockGroups)) {
      if (items.length === 0) continue;

      // Only consider groups that contain shapes (Location shapes or MapOverlayShape)
      const shapeItems = items.filter(item =>
        (item.field === 'Location' && item.type === 'shape') ||
        item.field === 'MapOverlayShape'
      );

      if (shapeItems.length === 0) continue;

      const firstShapeItem = shapeItems[0];

      if (shapeItems.length === 1) {
        // Single shape: operator === 'Eq' means within group
        if (firstShapeItem.operator === 'Eq') {
          withinGroups.push({
            blockGroupId: parseInt(blockGroupId),
            items: items // Keep all items for context, but we'll filter shapes when moving
          });
        }
      } else {
        // Multiple shapes: operator === 'Eq' AND 2nd shape's conjunction === 'Or'
        const secondShapeItem = shapeItems[1];
        if (firstShapeItem.operator === 'Eq' && secondShapeItem.conjunction === 'Or') {
          withinGroups.push({
            blockGroupId: parseInt(blockGroupId),
            items: items // Keep all items for context, but we'll filter shapes when moving
          });
        }
      }
    }

    return withinGroups;
  },

  // --------------------------------------------------------------------------
  // Configure the shape expression based on where it's being added
  // --------------------------------------------------------------------------
  _configureShapeExpression: function(shapeExpression, targetBlockGroup, targetGroupItems, allExpressions, insertAfterIndex) {
    const isNewBlockGroup = !targetGroupItems || targetGroupItems.length === 0;

    if (isNewBlockGroup) {
      // Creating a new block group
      shapeExpression.block_group = targetBlockGroup;
      shapeExpression.level = 1;
      shapeExpression.conjunction_level = 0;
      shapeExpression.conjunction = 'And'; // First item in block group always uses 'And' to connect to rest of filter
      // Operator defaults to 'Eq' (within) - this can be changed later
      if (!shapeExpression.operator) {
        shapeExpression.operator = 'Eq';
      }
    } else {
      // Adding to existing block group
      const firstItem = targetGroupItems[0];
      const lastItem = targetGroupItems[targetGroupItems.length - 1];

      shapeExpression.block_group = targetBlockGroup;
      shapeExpression.level = firstItem.level;
      shapeExpression.operator = firstItem.operator;

      // Determine conjunction based on block group type
      if (targetGroupItems.length === 1) {
        // Adding second item to a single-item group
        // If operator is 'Eq', use 'Or' (within group)
        // If operator is 'Ne', use 'And' (not within group)
        shapeExpression.conjunction = firstItem.operator === 'Eq' ? 'Or' : 'And';
        shapeExpression.conjunction_level = 1;
      } else {
        // Adding to multi-item group
        // Check if this is an intersection group (all use 'And')
        const isIntersection = targetGroupItems.every(item => item.conjunction === 'And');

        if (isIntersection) {
          shapeExpression.conjunction = 'And';
        } else {
          // Use the same conjunction as the last item
          shapeExpression.conjunction = lastItem.conjunction;
        }
        shapeExpression.conjunction_level = lastItem.conjunction_level;
      }
    }
  }
};

export default Filter;
